"use client";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Image from "next/image";
import NextLink from "next/link";
import React from "react";
import { toAssetImages } from "src/lib/utils";
import { luxeFloat, luxeGradientShift } from "../luxe-shared/keyframes";

export interface LuxeCta {
  label: string;
  href: string;
}

export interface FloatImage {
  src: string;
  alt: string;
}

export interface PageBannerLuxeProps {
  badge?: string;
  titleLine1?: string;
  titleLine2?: string;
  subtitle?: string;
  primaryCta?: LuxeCta;
  secondaryCta?: LuxeCta;
  floatRight?: FloatImage;
  floatLeft?: FloatImage;
}

const CtaLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => {
  if (href.startsWith("#")) {
    return (
      <Button
        component="a"
        href={href}
        variant="contained"
        size="large"
        sx={{
          borderRadius: 999,
          px: 6,
          py: 2.5,
          fontWeight: 900,
          fontSize: "1.1rem",
          bgcolor: "primary.main",
          color: "#fff",
          boxShadow: "0 12px 40px -8px rgba(0, 234, 211, 0.35)",
          "&:hover": {
            bgcolor: "primary.main",
            transform: "scale(1.05)",
            boxShadow: "0 16px 48px -8px rgba(0, 234, 211, 0.45)",
          },
        }}
      >
        {children}
      </Button>
    );
  }
  return (
    <NextLink href={href} passHref legacyBehavior>
      <Button
        component="a"
        variant="contained"
        size="large"
        sx={{
          borderRadius: 999,
          px: 6,
          py: 2.5,
          fontWeight: 900,
          fontSize: "1.1rem",
          bgcolor: "primary.main",
          color: "#fff",
          boxShadow: "0 12px 40px -8px rgba(0, 234, 211, 0.35)",
          "&:hover": {
            bgcolor: "primary.main",
            transform: "scale(1.05)",
            boxShadow: "0 16px 48px -8px rgba(0, 234, 211, 0.45)",
          },
        }}
      >
        {children}
      </Button>
    </NextLink>
  );
};

const PageBannerLuxe: React.FC<PageBannerLuxeProps> = ({
  badge = "Future-Ready Seafood",
  titleLine1 = "Đánh thức",
  titleLine2 = "Bản năng biển",
  subtitle = "",
  primaryCta = { label: "MUA NGAY", href: "#products" },
  secondaryCta = { label: "KHÁM PHÁ", href: "#products" },
  floatRight,
  floatLeft,
}) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const primary = theme.palette.primary.main;

  return (
    <Box
      component="header"
      sx={{
        position: "relative",
        minHeight: { xs: "80vh", md: "90vh" },
        display: "flex",
        alignItems: "center",
        pt: { xs: 12, md: 14 },
        overflow: "hidden",
        background: "linear-gradient(-45deg, #00ead3, #00f2fe, #7000ff, #ff4d6d)",
        backgroundSize: "400% 400%",
        animation: `${luxeGradientShift} 10s ease infinite`,
        maskImage: "linear-gradient(to bottom, black 80%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to bottom, black 80%, transparent 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(255,255,255,0.4)",
          backdropFilter: "blur(2px)",
        }}
      />

      {floatRight && isMd ? (
        <Box
          sx={{
            position: "absolute",
            top: "20%",
            right: "10%",
            width: 256,
            height: 256,
            opacity: 0.9,
            animation: `${luxeFloat} 6s ease-in-out infinite`,
            display: { xs: "none", md: "block" },
          }}
        >
          <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            <Image
              src={toAssetImages(floatRight.src)}
              alt={floatRight.alt}
              fill
              sizes="224px"
              style={{
                objectFit: "contain",
                mixBlendMode: "multiply",
                filter: "contrast(1.1)",
              }}
            />
          </Box>
        </Box>
      ) : null}

      {floatLeft && isMd ? (
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "8%",
            width: 224,
            height: 224,
            opacity: 0.9,
            animation: `${luxeFloat} 6s ease-in-out infinite`,
            animationDelay: "-3s",
            display: { xs: "none", md: "block" },
          }}
        >
          <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            <Image
              src={toAssetImages(floatLeft.src)}
              alt={floatLeft.alt}
              fill
              sizes="224px"
              style={{
                objectFit: "contain",
                mixBlendMode: "multiply",
                filter: "contrast(1.1)",
              }}
            />
          </Box>
        </Box>
      ) : null}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
        <Stack spacing={4} alignItems="center" sx={{ maxWidth: 900, mx: "auto" }}>
          <Box
            sx={{
              display: "inline-block",
              px: 2,
              py: 0.75,
              borderRadius: 999,
              border: `1px solid ${primary}4d`,
              bgcolor: "rgba(255,255,255,0.5)",
              color: primary,
              fontWeight: 800,
              fontSize: "10px",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              boxShadow: 1,
            }}
          >
            {badge}
          </Box>

          <Typography
            component="h1"
            sx={{
              fontWeight: 900,
              color: "text.primary",
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              fontSize: { xs: "2.5rem", md: "5rem" },
            }}
          >
            {titleLine1}
            <br />
            <Box
              component="span"
              sx={{
                color: primary,
                fontStyle: "italic",
                textShadow: "0 0 15px rgba(0, 234, 211, 0.4)",
              }}
            >
              {titleLine2}
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: { xs: "1rem", md: "1.25rem" },
              maxWidth: 640,
              mx: "auto",
              fontWeight: 500,
            }}
          >
            {subtitle}
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            justifyContent="center"
            sx={{ pt: 2 }}
          >
            <CtaLink href={primaryCta.href}>{primaryCta.label}</CtaLink>
            {secondaryCta.href.startsWith("#") ? (
              <Button
                component="a"
                href={secondaryCta.href}
                size="large"
                sx={{
                  borderRadius: 999,
                  px: 6,
                  py: 2.5,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: "text.primary",
                  bgcolor: "rgba(255,255,255,0.8)",
                  backdropFilter: "blur(16px)",
                  border: `1px solid ${primary}33`,
                  boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)",
                  "&:hover": {
                    borderColor: `${primary}80`,
                    bgcolor: "rgba(255,255,255,0.95)",
                  },
                }}
              >
                {secondaryCta.label}
              </Button>
            ) : (
              <NextLink href={secondaryCta.href} passHref legacyBehavior>
                <Button
                  component="a"
                  size="large"
                  sx={{
                    borderRadius: 999,
                    px: 6,
                    py: 2.5,
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color: "text.primary",
                    bgcolor: "rgba(255,255,255,0.8)",
                    backdropFilter: "blur(16px)",
                    border: `1px solid ${primary}33`,
                    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)",
                    "&:hover": {
                      borderColor: `${primary}80`,
                      bgcolor: "rgba(255,255,255,0.95)",
                    },
                  }}
                >
                  {secondaryCta.label}
                </Button>
              </NextLink>
            )}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default PageBannerLuxe;
